/**
 * /inspections/new — formulario mobile-first con checklist táctil.
 */
import { useMemo, useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import { ApiError, createInspection, listChecklistTemplates, listVehicles } from '@/lib/api'
import { uploadDocument } from '@/lib/upload'
import { PageHeader } from '@/components/app-shell'
import { Button } from '@/components/ui/button'
import { Card, CardBody, CardHeader } from '@/components/ui/card'
import { Field } from '@/components/ui/field'
import { cn } from '@/lib/utils'

type DraftItem = {
  key: string
  category: string
  label: string
  condition: 'Good' | 'Attention' | 'Repair' | 'Not Applicable'
  notes: string
  measurementUnit: string | null
}

const CONDITIONS = [
  { value: 'Good' as const, label: 'Good' },
  { value: 'Attention' as const, label: 'Attention' },
  { value: 'Repair' as const, label: 'Repair' },
]

export function InspectionNewPage() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const vehiclesQ = useQuery({ queryKey: ['vehicles'], queryFn: () => listVehicles() })
  const templatesQ = useQuery({
    queryKey: ['checklist-templates'],
    queryFn: listChecklistTemplates,
  })

  const template = templatesQ.data?.templates[0]
  const [vehicleId, setVehicleId] = useState('')
  const [inspectionType, setInspectionType] = useState('Pre-Trip')
  const [odometer, setOdometer] = useState('')
  const [locationText, setLocationText] = useState('')
  const [notes, setNotes] = useState('')
  const [formError, setFormError] = useState<string | null>(null)
  const [fields, setFields] = useState<Record<string, string>>({})
  // La foto no se puede subir hasta que la inspección exista: el endpoint la
  // adjunta a un id. Así que aquí solo se guarda el File y la subida ocurre
  // después de crearla, dentro de la mutación.
  const [photoFile, setPhotoFile] = useState<File | null>(null)
  const [photoWarning, setPhotoWarning] = useState<string | null>(null)

  const items = useMemo<DraftItem[]>(() => {
    const source =
      template?.items ??
      ([
        { category: 'Tire', label: 'L Front', measurementUnit: '32nds', sortOrder: 10 },
        { category: 'Tire', label: 'L Rear', measurementUnit: '32nds', sortOrder: 20 },
        { category: 'Tire', label: 'R Front', measurementUnit: '32nds', sortOrder: 30 },
        { category: 'Tire', label: 'R Rear', measurementUnit: '32nds', sortOrder: 40 },
      ] as const)
    return source.map((it, i) => ({
      key: `${it.category}-${it.label}-${i}`,
      category: it.category,
      label: it.label,
      condition: 'Good',
      notes: '',
      measurementUnit: it.measurementUnit ?? null,
    }))
  }, [template])

  const [draftItems, setDraftItems] = useState<DraftItem[] | null>(null)
  const checklist = draftItems ?? items

  const mutation = useMutation({
    mutationFn: async (input: Parameters<typeof createInspection>[0]) => {
      const res = await createInspection(input)

      let photoError: string | null = null
      if (photoFile) {
        try {
          await uploadDocument({
            file: photoFile,
            attachableType: 'inspection',
            attachableId: res.inspection.id,
            field: 'general_photo_url',
            description: 'Foto general de la inspección',
          })
        } catch (err) {
          // La inspección YA está guardada: fallar aquí haría que el inspector
          // repitiera el checklist entero por una foto. Se registra el motivo y
          // se decide en onSuccess.
          photoError =
            err instanceof ApiError
              ? `La inspección se guardó, pero la foto no: ${err.message}`
              : 'La inspección se guardó, pero la foto no se pudo subir.'
        }
      }

      return { ...res, photoError }
    },
    onSuccess: async (res) => {
      await qc.invalidateQueries({ queryKey: ['inspections'] })

      // Si la foto falló, NO se navega: al cambiar de pantalla el aviso
      // desaparecería y el inspector se iría creyendo que subió la foto.
      if (res.photoError) {
        setPhotoWarning(res.photoError)
        return
      }

      void navigate({
        to: '/inspections/$inspectionId',
        params: { inspectionId: res.inspection.id },
      })
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setFormError(err.message)
        setFields(err.fields ?? {})
      } else {
        setFormError('No se pudo guardar.')
      }
    },
  })

  function setCondition(key: string, condition: DraftItem['condition']) {
    setDraftItems((prev) => {
      const base = prev ?? items
      return base.map((it) => (it.key === key ? { ...it, condition } : it))
    })
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    setFormError(null)
    setFields({})
    if (!vehicleId) {
      setFields({ vehicleId: 'Selecciona un vehículo' })
      return
    }
    mutation.mutate({
      vehicleId,
      inspectionType,
      odometer: odometer ? Number(odometer) : null,
      locationText: locationText || null,
      notes: notes || null,
      items: checklist.map((it, i) => ({
        category: it.category,
        label: it.label,
        condition: it.condition,
        notes: it.notes || null,
        measurementUnit: it.measurementUnit,
        sortOrder: (i + 1) * 10,
      })),
    })
  }

  return (
    <>
      <PageHeader
        title="New inspection"
        subtitle="Checklist táctil · objetivos ≥ 44 px"
        actions={
          <Link to="/inspections" className="text-sm text-muted-foreground hover:text-foreground">
            Cancelar
          </Link>
        }
      />

      <form onSubmit={onSubmit} className="mx-auto max-w-xl space-y-4">
        {formError && (
          <p role="alert" className="text-sm font-medium text-danger">
            {formError}
          </p>
        )}

        <Card>
          <CardHeader title="Datos generales" />
          <CardBody className="space-y-3">
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium">
                Vehículo <span className="text-danger">*</span>
              </label>
              <select
                required
                value={vehicleId}
                onChange={(e) => setVehicleId(e.target.value)}
                className={cn(
                  'h-11 rounded-lg border bg-card px-3 text-sm',
                  fields.vehicleId ? 'border-danger' : 'border-input',
                )}
              >
                <option value="">Seleccionar…</option>
                {(vehiclesQ.data?.vehicles ?? []).map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.unitNumber}
                    {v.make ? ` · ${v.make}` : ''}
                  </option>
                ))}
              </select>
              {fields.vehicleId && (
                <p role="alert" className="text-xs font-medium text-danger">
                  {fields.vehicleId}
                </p>
              )}
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium">Tipo</label>
              <select
                value={inspectionType}
                onChange={(e) => setInspectionType(e.target.value)}
                className="h-11 rounded-lg border border-input bg-card px-3 text-sm"
              >
                {[
                  'Pre-Trip',
                  'Post-Trip',
                  'Daily',
                  'Weekly',
                  'Monthly',
                  'Safety',
                  'Maintenance',
                  'Custom',
                ].map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>

            <Field
              label="Odómetro"
              type="number"
              inputMode="decimal"
              value={odometer}
              onChange={(e) => setOdometer(e.target.value)}
              error={fields.odometer}
            />
            <Field
              label="Ubicación"
              value={locationText}
              onChange={(e) => setLocationText(e.target.value)}
            />
            <Field label="Notas" value={notes} onChange={(e) => setNotes(e.target.value)} />
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium">Foto general (cámara)</label>
              <input
                type="file"
                accept="image/*"
                capture="environment"
                className="block w-full text-sm file:mr-3 file:rounded-lg file:border-0 file:bg-primary file:px-3 file:py-2 file:text-sm file:font-medium file:text-primary-foreground"
                onChange={(e) => {
                  setPhotoFile(e.target.files?.[0] ?? null)
                  setPhotoWarning(null)
                }}
              />
              <p className="text-xs text-muted-foreground">
                {photoFile
                  ? `${photoFile.name} · se comprime a 1600 px antes de subir`
                  : 'Usa la cámara trasera en móvil. Se adjunta al guardar la inspección.'}
              </p>
              {photoWarning && (
                <p role="alert" className="text-xs font-medium text-warning-foreground">
                  {photoWarning}
                </p>
              )}
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardHeader title={template?.name ?? 'Checklist'} />
          <CardBody className="space-y-4">
            {checklist.map((it) => (
              <div key={it.key} className="space-y-2 border-b border-border pb-4 last:border-0 last:pb-0">
                <p className="text-sm font-medium">
                  {it.category} · {it.label}
                </p>
                <div className="grid grid-cols-3 gap-2">
                  {CONDITIONS.map((c) => (
                    <button
                      key={c.value}
                      type="button"
                      onClick={() => setCondition(it.key, c.value)}
                      className={cn(
                        'min-h-11 cursor-pointer rounded-xl border px-2 text-sm font-medium transition-colors',
                        it.condition === c.value
                          ? c.value === 'Good'
                            ? 'border-success bg-success-muted text-[oklch(45%_0.14_155)]'
                            : c.value === 'Attention'
                              ? 'border-warning bg-warning-muted text-warning-foreground'
                              : 'border-danger bg-danger-muted text-[oklch(45%_0.19_27)]'
                          : 'border-border bg-card text-muted-foreground hover:bg-muted',
                      )}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </CardBody>
        </Card>

        <Button type="submit" className="w-full" size="lg" loading={mutation.isPending}>
          Guardar inspección
        </Button>
      </form>
    </>
  )
}
